import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Banner,
  Button,
  FilterChips,
  IdChip,
  RewardPill,
  SearchInput,
  Select,
  StatusPill,
  Table,
  TCell,
  THead,
  TRow,
} from '@cou/admin-ui';
import { tierLabel } from '../awards/tier-label';
import { toRewardType } from '../contests/reward-cell';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  AWARD_STATES,
  defaultAwardsClient,
  type AdminAward,
  type AwardsClient,
} from '../awards/awards-client';
import {
  defaultContestsClient,
  type AdminContestWithEntryCount,
  type ContestsClient,
} from '../contests/contests-client';
import { awardPillState } from '../awards/award-state';
import { formatDateParts } from '../contests/format-date';

/**
 * The Awards section (API-23, admin-ui.md → Awards): the global delivery queue with its state
 * filters, an account-ref search and a contest filter, and the one operator-owned transition —
 * Retry on a Failed Award (GL-13).
 *
 * Its own nav section rather than a Settlement tab (decision 3): OPS_SUPPORT holds `awards:view`
 * without `contests:settle`, so answering "player says no bonus" must not require entering the
 * settlement tooling. Retry is separately gated on `awards:retry`, so the same operator can read
 * the queue without being able to act on it.
 */

export interface AwardsScreenProps {
  /** Injected for tests; defaults to the real API-23 client. */
  readonly client?: AwardsClient;
  /** Injected for tests; sources the contest-filter dropdown (API-09). */
  readonly contestsClient?: ContestsClient;
}

const STATE_CHIPS = [
  { key: 'all', label: 'All' },
  ...AWARD_STATES.map((state) => ({ key: state, label: state })),
];

/** How long to let an operator keep typing an account ref before asking the server. */
const SEARCH_DEBOUNCE_MS = 250;

/** How many contests to offer in the filter dropdown (a dropdown, not chips, so it scales). */
const CONTEST_OPTION_LIMIT = 100;

/** A recognised Award state from the `?status=` query, or 'all' — so a hand-typed value can't
 * become a filter that matches nothing. This is how the Settlement strip's failed-award link
 * (`/awards?status=Failed`) lands pre-filtered. */
function initialStatus(raw: string | null): string {
  return raw !== null && (AWARD_STATES as readonly string[]).includes(raw) ? raw : 'all';
}

/**
 * The Award's `prizeType` as admin-ui's RewardPill spells it, reusing the contest editor's mapper
 * so a reward wears the same colour wherever an operator meets it. An unrecognised type falls back
 * to the FreeBet pill rather than dropping the reward off the row — the label carries the truth.
 */
function rewardPillType(prizeType: string) {
  return toRewardType(prizeType) ?? 'freebet';
}

export function AwardsScreen({
  client = defaultAwardsClient,
  contestsClient = defaultContestsClient,
}: AwardsScreenProps) {
  const { getToken, claims } = useAdminAuth();
  const [searchParams] = useSearchParams();
  // Seed the filter from the URL once, so the Settlement strip's failed-award link arrives
  // pre-filtered; thereafter the chips own it (the query param is not kept in sync).
  const [status, setStatus] = useState(() => initialStatus(searchParams.get('status')));
  const [contestFilter, setContestFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [contests, setContests] = useState<readonly AdminContestWithEntryCount[]>([]);
  const [awards, setAwards] = useState<AdminAward[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mayRetry = claims?.permissions.includes('awards:retry') ?? false;

  // Debounce the account search so typing doesn't fire a round-trip per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // The contest-filter source loads once.
  useEffect(() => {
    const token = getToken();
    if (token === null) return;
    let cancelled = false;
    void (async () => {
      const result = await contestsClient.listContests(token, { limit: CONTEST_OPTION_LIMIT });
      if (!cancelled && result.ok) setContests(result.contests);
    })();
    return () => {
      cancelled = true;
    };
  }, [contestsClient, getToken]);

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null) return;
    const result = await client.listAwards(token, {
      ...(status === 'all' ? {} : { status }),
      ...(contestFilter === 'all' ? {} : { contestId: contestFilter }),
      ...(debouncedSearch ? { account: debouncedSearch } : {}),
    });
    if (result.ok) {
      setAwards(result.awards);
      setError(null);
    } else {
      setError(result.message ?? 'The awards lookup failed.');
      setAwards(null);
    }
  }, [client, contestFilter, debouncedSearch, getToken, status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onRetry(award: AdminAward) {
    const token = getToken();
    if (token === null) return;
    const result = await client.retryAward(token, award.id);
    if (result.ok) {
      // Swap in the returned row rather than refetching the page: the operator keeps their place
      // in the queue and sees the state they just caused.
      setAwards((current) =>
        current === null ? current : current.map((a) => (a.id === award.id ? result.award : a)),
      );
      setError(null);
    } else {
      // A 409 means the pump claimed it, or another operator got there first. Say so — the
      // operator's mental model of the queue is now wrong, and silently doing nothing is worse.
      setError(result.message ?? 'The retry failed.');
    }
  }

  const contestOptions = [
    { value: 'all', label: 'All contests' },
    ...contests.map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <AdminShell
      title="Awards"
      subtitle="Reward delivery across contests. Retry returns a failed award to the pump."
    >
      {/* Chips on the left, the contest dropdown and account search on the right — all label-less
          (each control names itself: "All contests", the "Account ref" placeholder) so the row
          aligns on one baseline. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterChips chips={STATE_CHIPS} active={status} onSelect={setStatus} />
        <div className="flex flex-shrink-0 items-center gap-2">
          <Select
            aria-label="Contest"
            className="w-[240px]"
            options={contestOptions}
            value={contestFilter}
            onChange={setContestFilter}
          />
          <SearchInput
            className="w-[240px]"
            pill={false}
            role="searchbox"
            aria-label="Search by account ref"
            placeholder="Account ref"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {error !== null && (
        <Banner data-testid="awards-error" variant="danger" className="mt-4">
          {error}
        </Banner>
      )}

      {awards !== null && awards.length === 0 && (
        <p data-testid="awards-empty" className="mt-8 text-center text-sm text-ink-muted">
          No award matches this filter.
        </p>
      )}

      {awards !== null && awards.length > 0 && (
        <Table
          className="mt-4"
          columns="140px 118px 88px 180px 112px 76px minmax(0,1.4fr) 110px 84px"
        >
          <THead>
            <TCell>Account ref</TCell>
            <TCell>Contest</TCell>
            <TCell>Tier</TCell>
            <TCell>Reward</TCell>
            <TCell>State</TCell>
            <TCell>Attempts</TCell>
            <TCell>Last error</TCell>
            <TCell>Delivered</TCell>
            <TCell />
          </THead>
          {awards.map((award) => (
            <TRow key={award.id} data-testid={`award-row-${award.id}`}>
              <TCell variant="mono">{award.account}</TCell>
              {/* admin-ui.md lists a "contest" column; the id chip (same as the contest list) links
                  to the contest detail in a new tab so an operator keeps their place in the queue. */}
              <TCell>
                <a
                  href={`/contests/${award.contestId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  <IdChip value={award.contestId.slice(0, 8)} title={award.contestId} />
                </a>
              </TCell>
              <TCell>{tierLabel(award.tier)}</TCell>
              <TCell>
                <RewardPill type={rewardPillType(award.rewardType)} label={award.displayValue} />
              </TCell>
              <TCell>
                <StatusPill kind="award" state={awardPillState(award.status)} />
              </TCell>
              <TCell variant="num">{award.attempts}</TCell>
              <TCell className="text-danger">{award.lastError ?? '—'}</TCell>
              <TCell>
                {award.deliveredAt === null ? '—' : formatDateParts(award.deliveredAt).date}
              </TCell>
              <TCell>
                {/* Only Failed is stuck awaiting a human: Delivered would double-credit, and the
                    rest are already moving under the pump (GL-13). */}
                {award.status === 'Failed' && mayRetry && (
                  <Button variant="outline" size="sm" onClick={() => void onRetry(award)}>
                    Retry
                  </Button>
                )}
              </TCell>
            </TRow>
          ))}
        </Table>
      )}
    </AdminShell>
  );
}
